import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useToast } from "@/hooks/use-toast";
import api from "@/lib/api";
import { useOrganizationsStore } from "@/stores/organizationsStore";
import { usePatientsStore } from "@/stores/patientsStore";
import {
  AlertCircle,
  CheckCircle,
  ChevronLeft,
  File,
  Upload,
  X,
} from "lucide-react";
import React, { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

// Define response type for ingestPatientFiles
interface PatientIngestResponse {
  organization_id: string;
  processed_files: any[];
}

const NewPatientPage: React.FC = () => {
  const navigate = useNavigate();
  const [files, setFiles] = useState<File[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadResults, setUploadResults] = useState<any[]>([]);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { toast } = useToast?.() || {
    toast: (props: any) => console.log(props),
  };

  const { selectedOrganizationId } = useOrganizationsStore();
  const { fetchPatientsForCurrentOrg } = usePatientsStore();

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files?.length) return;

    const fileList = Array.from(event.target.files);

    // Validate files are PDFs
    const nonPdfFiles = fileList.filter(
      (file) => !file.name.toLowerCase().endsWith(".pdf")
    );

    if (nonPdfFiles.length > 0) {
      toast({
        title: "Invalid file type",
        description: "Only PDF files are allowed",
        variant: "destructive",
      });
      return;
    }

    setFiles((prev) => [...prev, ...fileList]);

    // Reset the input so the same file can be selected again if removed
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const removeFile = (index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (e.dataTransfer.files?.length) {
      const fileList = Array.from(e.dataTransfer.files);

      // Validate files are PDFs
      const nonPdfFiles = fileList.filter(
        (file) => !file.name.toLowerCase().endsWith(".pdf")
      );

      if (nonPdfFiles.length > 0) {
        toast({
          title: "Invalid file type",
          description: "Only PDF files are allowed",
          variant: "destructive",
        });
        return;
      }

      setFiles((prev) => [...prev, ...fileList]);
    }
  };

  const handleSubmit = async () => {
    if (!selectedOrganizationId) {
      toast({
        title: "Error",
        description:
          "No organization selected. Please select an organization first.",
        variant: "destructive",
      });
      return;
    }

    if (!files.length) {
      toast({
        title: "No files selected",
        description: "Please select at least one PDF file to upload.",
        variant: "destructive",
      });
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);
    setUploadError(null);
    setUploadResults([]);

    // Declare interval variable outside try/catch so it's available in both blocks
    let progressInterval: NodeJS.Timeout | null = null;

    try {
      // Create FormData
      const formData = new FormData();
      files.forEach((file) => {
        formData.append("files", file);
      });

      // Mock progress updates
      progressInterval = setInterval(() => {
        setUploadProgress((prev) => {
          const newProgress = prev + 5;
          return newProgress >= 90 ? 90 : newProgress; // Cap at 90% until complete
        });
      }, 500);

      // Call the API endpoint
      const response = (await api.ingestPatientFiles(
        selectedOrganizationId,
        formData
      )) as PatientIngestResponse;

      clearInterval(progressInterval);
      setUploadProgress(100);
      setUploadResults(response.processed_files || []);

      toast({
        title: "Upload successful",
        description: `Successfully processed ${
          response.processed_files?.length || 0
        } file(s).`,
      });

      // Refresh the patients list
      fetchPatientsForCurrentOrg();

      // Wait a moment before redirecting
      setTimeout(() => {
        navigate("/patients");
      }, 3000);
    } catch (error: any) {
      if (progressInterval) clearInterval(progressInterval);
      setUploadProgress(0);
      console.error("Error uploading files:", error);
      setUploadError(
        error?.response?.data?.detail ||
          "Failed to upload files. Please try again."
      );

      toast({
        title: "Upload failed",
        description:
          "There was an error processing your files. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="mx-auto max-w-3xl my-4 px-4 md:my-8 md:px-0">
      <Button
        variant="outline"
        className="mb-6 flex items-center gap-2"
        onClick={() => navigate("/patients")}
      >
        <ChevronLeft className="h-4 w-4" />
        Back to Patients
      </Button>

      <Card>
        <CardHeader>
          <CardTitle>Import Patient Records</CardTitle>
        </CardHeader>
        <CardContent>
          {!isUploading ? (
            <>
              <div
                className="border-2 border-dashed rounded-lg p-6 md:p-8 text-center mb-6 cursor-pointer hover:bg-muted/50 dark:hover:bg-foreground transition-colors"
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf"
                  multiple
                  onChange={handleFileChange}
                  className="hidden"
                />
                <Upload className="h-10 w-10 mx-auto mb-2 text-muted-foreground/60" />
                <h3 className="font-medium mb-1">Drag and drop files here</h3>
                <p className="text-sm text-muted-foreground mb-3">Or click to browse</p>
                <p className="text-xs text-muted-foreground/60">
                  Only PDF files are accepted
                </p>
              </div>

              {files.length > 0 && (
                <div className="mb-6">
                  <h3 className="font-medium mb-3">
                    Selected Files ({files.length})
                  </h3>
                  <ScrollArea className="h-[200px] w-full border rounded-md p-2">
                    <div className="space-y-2">
                      {files.map((file, index) => (
                        <div
                          key={index}
                          className="flex items-center justify-between border rounded p-2 bg-muted/50 dark:bg-foreground"
                        >
                          <div className="flex items-center gap-2 overflow-hidden">
                            <File className="h-4 w-4 flex-shrink-0" />
                            <span className="text-sm truncate">
                              {file.name}
                            </span>
                            <span className="text-xs text-muted-foreground">
                              ({(file.size / 1024).toFixed(1)} KB)
                            </span>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 w-6 p-0"
                            onClick={(e) => {
                              e.stopPropagation();
                              removeFile(index);
                            }}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                </div>
              )}

              <Button
                className="w-full"
                disabled={files.length === 0 || !selectedOrganizationId}
                onClick={handleSubmit}
              >
                Process Patient Files
              </Button>

              {!selectedOrganizationId && (
                <p className="text-sm text-destructive text-center mt-2">
                  Please select an organization first before uploading files.
                </p>
              )}
            </>
          ) : (
            <div className="space-y-4">
              <h3 className="font-medium">Uploading and processing files...</h3>
              <Progress value={uploadProgress} className="h-2" />
              <p className="text-sm text-muted-foreground text-center">
                {uploadProgress}% complete
              </p>
            </div>
          )}

          {uploadError && (
            <Alert variant="destructive" className="mt-6">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Error</AlertTitle>
              <AlertDescription>{uploadError}</AlertDescription>
            </Alert>
          )}

          {uploadResults.length > 0 && (
            <Alert className="mt-6 bg-success/10 dark:bg-green-900/20">
              <CheckCircle className="h-4 w-4 text-success dark:text-success" />
              <AlertTitle>Success</AlertTitle>
              <AlertDescription>
                Successfully processed {uploadResults.length} file(s).
                Redirecting to patients list...
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default NewPatientPage;
